import { useEffect, useState, useCallback } from 'react';

import type { Tables } from '@/integrations/supabase/types';
import type { AuthData } from '@/utils/authHelpers';
import { hasAuthToken } from '@/utils/authToken';
import { useMemoryLeakDetector } from '@/hooks/useMemoryLeakDetector';

export type User = Tables<'profiles'> & {
  is_admin?: boolean;
  is_marketer?: boolean;
  role?: string;
};

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const { safeAsync, isMounted } = useMemoryLeakDetector('useAuth');

  // Create a version of safeAsync that allows auth operations to complete after unmount
  // since auth state is global and should persist across component remounts
  const safeAuthAsync = useCallback(
    async function<T>(promise: Promise<T>, abortSignal?: AbortSignal): Promise<T | null> {
      return safeAsync(promise, abortSignal, true);
    },
    [safeAsync]
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    let isMountedFlag = true;
    let removeRateLimitListener: (() => void) | undefined;
    let cleanupAuth: (() => void) | undefined;
    let abortController = new AbortController();

    const initialize = async () => {
      const tokenPresent = hasAuthToken();
      if (!tokenPresent) {
        setUser(null);
        return;
      }

      try {
        const { initializeAuth } = await import('@/utils/authHelpers');

        const handleRateLimit = (event: Event) => {
          try {
            const customEvent = event as CustomEvent;
            const { duration } = customEvent.detail as { duration?: number };
            if (!duration) return undefined;

            const minutes = Math.ceil(duration / 60000);

            const notificationDiv = document.createElement('div');
            notificationDiv.style.cssText = `
              position: fixed;
              top: var(--space-lg, 24px);
              left: 50%;
              transform: translateX(-50%);
              background: var(--chart-red, #ef4444);
              color: var(--white, #ffffff);
              padding: var(--space-md, 16px) var(--space-lg, 24px);
              border-radius: var(--radius-lg);
              box-shadow: var(--shadow-lg);
              z-index: var(--z-overlay);
              font-weight: 500;
              max-width: 90vw;
              text-align: center;
            `;
            notificationDiv.innerHTML = `
              <div>⚠️ Server Busy - Please Wait</div>
              <div style="font-size: var(--text-sm, 14px); margin-top: var(--space-xs, 4px); opacity: 0.9;">
                Please wait ${minutes} minute${minutes > 1 ? 's' : ''} before refreshing.
                Our servers are handling high traffic.
              </div>
            `;

            document.body.appendChild(notificationDiv);

            const timeoutId = setTimeout(
              () => {
                try {
                  notificationDiv.parentNode?.removeChild(notificationDiv);
                } catch {
                  // Error removing notification
                }
              },
              Math.min(Number(duration) || 0, 30000)
            );

            return () => {
              clearTimeout(timeoutId);
              try {
                notificationDiv.parentNode?.removeChild(notificationDiv);
              } catch {
                // Error cleaning up notification
              }
            };
          } catch {
            // Error handling rate limit notification
            return undefined;
          }
        };

        window.addEventListener('rateLimitActive', handleRateLimit);
        removeRateLimitListener = () =>
          window.removeEventListener('rateLimitActive', handleRateLimit);

        const handleAuthData = (authData: AuthData) => {
          if (!isMountedFlag) return;

          try {
            // authData contains: { user, session, profile, roles }
            if (authData.session?.user) {
              if (authData.profile && authData.roles) {
                // Merge profile data with role information
                const userWithRoles = {
                  ...authData.profile,
                  is_admin: authData.roles.includes('admin'),
                  is_marketer: authData.roles.includes('marketer'),
                  role: authData.roles.includes('admin')
                    ? 'admin'
                    : authData.roles.includes('marketer')
                      ? 'marketer'
                      : authData.roles.includes('partner')
                        ? 'partner'
                        : 'user',
                } as User;
                setUser(userWithRoles);
              } else {
                // If we have a session but no profile/roles, create a basic user object
                // This handles cases where profile fetch fails but user is authenticated
                const basicUser = {
                  id: authData.session.user.id,
                  email: authData.session.user.email ?? '',
                  full_name:
                    authData.session.user.user_metadata?.full_name ??
                    authData.session.user.user_metadata?.name ??
                    authData.session.user.email?.split('@')[0] ??
                    'User',
                  profile_completed: false,
                  is_admin: false,
                  is_marketer: false,
                  role: 'user' as const,
                  // Add other required fields with defaults
                  phone: null,
                  age: null,
                  country: null,
                  gender: null,
                  profile_image_url: null,
                  preferred_languages: [],
                  allergies: [],
                  current_medications: [],
                  chronic_conditions: [],
                  past_surgeries: [],
                  family_history: null,
                  smoking_status: null,
                  alcohol_consumption: null,
                  exercise_frequency: null,
                  height_cm: null,
                  weight_kg: null,
                  blood_type: null,
                  emergency_contact_name: null,
                  emergency_contact_phone: null,
                  emergency_contact_relationship: null,
                  is_partner: false,
                  assigned_provider_ids: [],
                  partner_permissions: {
                    can_edit_provider_info: true,
                    can_edit_treatments: true,
                    can_edit_doctors: true,
                    can_view_inquiries: true,
                    can_respond_to_inquiries: true,
                  },
                  partner_category: null,
                  admin_memo: null,
                  bank_accounts: null,
                  third_party_services: [],
                  transportation_types: [],
                  created_at: null,
                  updated_at: null,
                } as User;
                setUser(basicUser);
              }
            } else {
              setUser(null);
            }
          } catch (error) {
            console.error('Error in auth callback:', error);
            // Error in auth callback
            setUser(null);
          }
        };

        cleanupAuth = initializeAuth(handleAuthData, safeAuthAsync, abortController.signal);

        // Immediately check if we should abort due to hot reloading or other issues
        if (abortController.signal.aborted) {
          cleanupAuth();
          return;
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      }
    };

    initialize().catch(error => {
      console.error('Auth initialization failed:', error);
    });

    return () => {
      isMountedFlag = false;
      abortController.abort();
      if (cleanupAuth) {
        cleanupAuth();
      }
      if (removeRateLimitListener) {
        removeRateLimitListener();
      }
    };
  }, [isMounted, safeAsync, safeAuthAsync]); // Auth state is global but these utilities are dependencies

  return { user, setUser };
};
